import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import {
  formatReportDate,
  formatReportMoney,
  getReportAuthorization,
  ReportECTransaction,
} from './reportEC.utils';

interface Props {
  rows: ReportECTransaction[];
  count: number;
  loading: boolean;
  error: boolean;
  limit: number;
  page: number;
  onRetry: () => void;
  onReset: () => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onReceipt: (id: string | number) => void;
  showIdentification?: boolean;
}

const Status = ({ value = 'Não informado' }: { value?: string }) => {
  const color =
    value === 'Aprovada'
      ? 'success'
      : /cancel|recus|negad|chargeback/i.test(value)
      ? 'error'
      : /pend|análise|process/i.test(value)
      ? 'warning'
      : '';
  return (
    <Chip
      label={value}
      size="small"
      sx={{
        height: 23,
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 1,
        bgcolor: color ? `${color}.light3` : 'action.hover',
        color: color ? `${color}.main` : 'text.secondary',
      }}
    />
  );
};

const Transaction = ({
  row,
  compact,
  onReceipt,
  showIdentification,
}: {
  row: ReportECTransaction;
  compact: boolean;
  onReceipt: Props['onReceipt'];
  showIdentification: boolean;
}) => {
  const [open, setOpen] = React.useState(false);
  const authorization = getReportAuthorization(row);
  const hasSplit = Array.isArray(row.Split) && row.Split.length > 0;
  const splitId = `report-ec-split-${row.CdTransacao}`;
  const actions = (
    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
      <Tooltip
        title={
          row.Status === 'Aprovada'
            ? 'Imprimir comprovante'
            : 'Comprovante disponível para transações aprovadas'
        }
      >
        <span>
          <IconButton
            aria-label={`Imprimir comprovante da transação ${
              authorization || row.CdTransacao
            }`}
            size="small"
            disabled={row.Status !== 'Aprovada'}
            onClick={() => onReceipt(row.CdTransacao)}
          >
            <PrintOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {hasSplit && (
        <Tooltip title="Detalhes do split">
          <IconButton
            size="small"
            aria-label={`Detalhes do split da transação ${
              authorization || row.CdTransacao
            }`}
            aria-expanded={open}
            aria-controls={splitId}
            onClick={() => setOpen(!open)}
          >
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms',
              }}
            />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
  const split = (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box id={splitId} p={1.5} bgcolor="action.hover">
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Divisão do pagamento
        </Typography>
        <Box display="grid" gap={1}>
          {row.Split?.map((item, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {item.NmClienteSplit || 'Receptor não informado'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.TpPagamento || '—'} · {item.QtdParcelas || 1} parcela(s)
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700}>
                {formatReportMoney(item.VlLiberado)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Collapse>
  );

  if (compact)
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box p={1.5}>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}
          >
            <Box minWidth={0}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ overflowWrap: 'anywhere' }}
              >
                NSU {authorization || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatReportDate(row.DtTransacao)}
              </Typography>
            </Box>
            <Status value={row.Status} />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            mt={1.5}
          >
            <Typography variant="body2" color="text.secondary">
              {row.TpPagamento || '—'} · {row.QtdParcelas || 1} parcela(s)
            </Typography>
            <Typography fontWeight={800}>
              {formatReportMoney(row.VlTransacao)}
            </Typography>
          </Box>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mt={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Valor líquido
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatReportMoney(row.VlLiberado)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Taxas
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatReportMoney(row.VlTaxas)}
              </Typography>
            </Box>
          </Box>
          {showIdentification && (
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap={1}
              mt={1.5}
              sx={{ overflowWrap: 'anywhere' }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Identificação do cliente
                </Typography>
                <Typography variant="body2">
                  {row.DsIdentificacaoCliente || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Identificação do serviço
                </Typography>
                <Typography variant="body2">
                  {row.DsIdentificacaoServico || '—'}
                </Typography>
              </Box>
            </Box>
          )}
          <Divider sx={{ mt: 1.5, mb: 0.5 }} />
          {actions}
        </Box>
        {hasSplit && split}
      </Paper>
    );

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {formatReportDate(row.DtTransacao)}
        </TableCell>
        <TableCell
          sx={{ fontWeight: 700, overflowWrap: 'anywhere', maxWidth: 180 }}
        >
          {authorization || '—'}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatReportMoney(row.VlTransacao)}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {formatReportMoney(row.VlLiberado)}
        </TableCell>
        <TableCell>{row.TpPagamento || '—'}</TableCell>
        <TableCell align="center">{row.QtdParcelas || '—'}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {formatReportMoney(row.VlTaxas)}
        </TableCell>
        <TableCell>
          <Status value={row.Status} />
        </TableCell>
        {showIdentification && (
          <>
            <TableCell sx={{ maxWidth: 160, overflowWrap: 'anywhere' }}>
              {row.DsIdentificacaoCliente || '—'}
            </TableCell>
            <TableCell sx={{ maxWidth: 160, overflowWrap: 'anywhere' }}>
              {row.DsIdentificacaoServico || '—'}
            </TableCell>
          </>
        )}
        <TableCell align="right">{actions}</TableCell>
      </TableRow>
      {hasSplit && (
        <TableRow>
          <TableCell
            colSpan={showIdentification ? 11 : 9}
            sx={{ p: 0, borderBottom: open ? undefined : 0 }}
          >
            {split}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const TableReportEC = ({
  rows,
  count,
  loading,
  error,
  limit,
  page,
  onRetry,
  onReset,
  onPageChange,
  onLimitChange,
  onReceipt,
  showIdentification = false,
}: Props) => {
  const compact = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  return (
    <Paper
      aria-busy={loading}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(17, 24, 39, 0.05)',
        overflow: 'hidden',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        p={1.5}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Typography component="h2" variant="subtitle2" fontWeight={700}>
          Transações
        </Typography>
        <Chip
          size="small"
          label={`${count.toLocaleString('pt-BR')} resultados`}
          sx={{
            height: 22,
            bgcolor: 'primary.light3',
            color: 'primary.main',
            fontWeight: 700,
            fontSize: 11,
          }}
        />
      </Box>
      {loading && <LinearProgress aria-label="Carregando transações" />}
      {error ? (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={onRetry}
              disabled={loading}
            >
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar as transações.
        </Alert>
      ) : loading ? (
        <Box p={2}>
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} height={48} />
          ))}
        </Box>
      ) : !rows.length ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
          py={6}
          px={2}
          textAlign="center"
        >
          <ReceiptLongIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Nenhuma transação encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tente outro período ou ajuste os filtros da pesquisa.
          </Typography>
          <Button size="small" onClick={onReset}>
            Limpar filtros
          </Button>
        </Box>
      ) : compact ? (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }}
          gap={1.25}
          p={1.5}
        >
          {rows.map((row) => (
            <Transaction
              key={row.CdTransacao}
              row={row}
              compact
              onReceipt={onReceipt}
              showIdentification={showIdentification}
            />
          ))}
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 'max(320px, calc(100vh - 410px))' }}>
          <Table
            stickyHeader
            size="small"
            aria-label="Relatório de transações"
            sx={{
              minWidth: 1000,
              '& .MuiTableCell-root': { fontSize: 12, px: 1.25, py: 1 },
              '& .MuiTableCell-head': {
                bgcolor: 'grey.50',
                color: 'text.secondary',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  'Data e hora',
                  'Autorização (NSU)',
                  'Valor transação',
                  'Valor líquido',
                  'Pagamento',
                  'Parcelas',
                  'Taxas',
                  'Status',
                  ...(showIdentification
                    ? ['Identificação do cliente', 'Identificação do serviço']
                    : []),
                  'Ações',
                ].map((label, index) => (
                  <TableCell
                    key={label}
                    align={
                      [2, 3, 6, showIdentification ? 10 : 8].includes(index)
                        ? 'right'
                        : index === 5
                        ? 'center'
                        : 'left'
                    }
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <Transaction
                  key={row.CdTransacao}
                  row={row}
                  compact={false}
                  onReceipt={onReceipt}
                  showIdentification={showIdentification}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!error && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
          getItemAriaLabel={(type) =>
            type === 'next'
              ? 'Próxima página'
              : type === 'previous'
              ? 'Página anterior'
              : type === 'first'
              ? 'Primeira página'
              : 'Última página'
          }
          onPageChange={(_, nextPage) => {
            if (!loading) onPageChange(nextPage);
          }}
          onRowsPerPageChange={(event) =>
            onLimitChange(Number(event.target.value))
          }
          SelectProps={{ disabled: loading }}
          nextIconButtonProps={{
            disabled: loading || error || (page + 1) * limit >= count,
          }}
          backIconButtonProps={{ disabled: loading || error || page === 0 }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': { px: { xs: 0.5, sm: 2 } },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              { fontSize: 12 },
            '& .MuiTablePagination-select': { pl: 0.5, pr: '20px !important' },
            '& .MuiTablePagination-actions': { ml: { xs: 0, sm: 2 } },
          }}
        />
      )}
    </Paper>
  );
};

export default TableReportEC;
